// Preset character gallery for the music-video character picker.
//
// The human roster REUSES the mobile app's established preset characters
// (mobile/app/(tabs)/music-video.tsx CHARACTER_OPTIONS — static assets under
// client/public/voice-avatars/gen-*.png, referenced by their absolute
// solmi.ai URLs). Mobile already passes exactly these URLs into the MV
// pipeline as characterUrl, so they are proven pipeline inputs. Keeping the
// two rosters identical also keeps web and mobile feeling like one product.
// Deliberately NOT copied from competitor galleries (an earlier roster
// mirrored freebeat's presets and was replaced).
//
// The two mascots are our own, generated by
// scripts/generate-character-presets.ts and hosted under
// character-presets/<id>-v<n>.png with immutable cache-control — a
// regenerated mascot image must bump the version suffix, never overwrite.
//
// The picker treats a preset exactly like an uploaded photo: selecting one
// sets the project's characterImageUrl.

export type CharacterPresetAppearance = "male" | "female" | "none";

export type CharacterPreset = {
  id: string;
  /** Display name shown under the thumbnail. */
  name: string;
  /**
   * Apparent presentation of the performer, used by the lead-photo vs
   * vocal-gender conflict check ("none" for mascots — they never conflict).
   */
  appearance: CharacterPresetAppearance;
  /** Genre affinity used to rank presets against the song's detected genre. */
  genres: string[];
  /** Hosted image URL (absolute — the render pipeline fetches it server-side). */
  imageUrl: string;
  /** Generation prompt — empty for assets reused from the mobile roster. */
  prompt: string;
};

/**
 * Mobile-roster asset. Originally served straight from the app origin
 * (client/public/voice-avatars); the gallery's 15-image concurrent burst
 * 502'd the Railway origin (2026-08-05), so optimized copies live on the
 * media CDN under versioned immutable keys (scripts/rehost-voice-avatars.ts
 * — a changed image must bump the -v suffix). The old
 * solmi.ai/voice-avatars/<file>.png URLs must stay live: shipped mobile
 * builds hardcode them.
 */
const voiceAvatar = (file: string) =>
  `https://media.solmi.ai/voice-avatars/${file}-v1.png`;

// NOTE: the generated mascots exist only on this bucket (not media.solmi.ai)
// — don't fold this into the voice-avatar base without re-hosting them.
const PRESET_IMAGE_BASE =
  "https://aisongmaker-bucket.s3.us-west-1.amazonaws.com/character-presets";

/** Generated preset image URL. Objects are immutable-cached, so any
 * regenerated image must be uploaded under a bumped version suffix. */
const presetImage = (id: string, version = 1) =>
  `${PRESET_IMAGE_BASE}/${id}-v${version}.png`;

const MASCOT_SUFFIX =
  "Adorable 3D character render, full body, centered, facing camera, single " +
  "character, plain soft-color studio backdrop, soft even lighting, high " +
  "detail, no text, no watermark, 3:4 portrait framing.";

export const CHARACTER_PRESETS: CharacterPreset[] = [
  {
    id: "lead-singer",
    name: "Lead Singer",
    appearance: "female",
    genres: ["pop", "dance", "electronic"],
    imageUrl: voiceAvatar("gen-lead-singer"),
    prompt: "",
  },
  {
    id: "pop-male-vocalist",
    name: "Male Vocalist",
    appearance: "male",
    genres: ["pop", "dance", "r&b"],
    imageUrl: voiceAvatar("gen-pop-male-vocalist"),
    prompt: "",
  },
  {
    id: "dream-vocalist",
    name: "Dream Vocalist",
    appearance: "female",
    genres: ["pop", "indie", "k-pop", "city pop"],
    imageUrl: voiceAvatar("gen-dream-vocalist"),
    prompt: "",
  },
  {
    id: "studio-vocalist",
    name: "Studio Vocalist",
    appearance: "male",
    genres: ["rock", "pop", "indie"],
    imageUrl: voiceAvatar("gen-studio-vocalist"),
    prompt: "",
  },
  {
    id: "lady-singer",
    name: "Lady Singer",
    appearance: "female",
    genres: ["pop", "dance"],
    imageUrl: voiceAvatar("gen-lady-singer"),
    prompt: "",
  },
  {
    id: "man-singer",
    name: "Man Singer",
    appearance: "male",
    genres: ["pop", "r&b", "hip hop", "soul"],
    imageUrl: voiceAvatar("gen-man-singer"),
    prompt: "",
  },
  {
    id: "pop-vocalist",
    name: "Pop Vocalist",
    appearance: "female",
    genres: ["pop", "dance", "country"],
    imageUrl: voiceAvatar("gen-lady-singer2"),
    prompt: "",
  },
  {
    id: "studio-singer",
    name: "Studio Singer",
    appearance: "male",
    genres: ["pop", "rock", "indie"],
    imageUrl: voiceAvatar("gen-man-singer2"),
    prompt: "",
  },
  {
    id: "rocker",
    name: "Rock Star",
    appearance: "male",
    genres: ["rock", "metal", "punk", "alternative"],
    imageUrl: voiceAvatar("gen-rocker"),
    prompt: "",
  },
  {
    id: "country-star",
    name: "Country Star",
    appearance: "female",
    genres: ["country", "folk", "americana"],
    imageUrl: voiceAvatar("gen-country-star-v2"),
    prompt: "",
  },
  {
    id: "country-singer",
    name: "Country Singer",
    appearance: "female",
    genres: ["country", "folk", "americana"],
    imageUrl: voiceAvatar("gen-country-singer"),
    prompt: "",
  },
  {
    id: "soul-singer",
    name: "Soul Singer",
    appearance: "male",
    genres: ["soul", "r&b", "gospel", "jazz", "blues"],
    imageUrl: voiceAvatar("gen-soul-singer"),
    prompt: "",
  },
  {
    id: "girl-singer",
    name: "Girl Singer",
    appearance: "female",
    genres: ["children", "pop"],
    imageUrl: voiceAvatar("gen-girl-singer"),
    prompt: "",
  },
  {
    id: "boy-singer",
    name: "Boy Singer",
    appearance: "male",
    genres: ["children", "pop"],
    imageUrl: voiceAvatar("gen-boy-singer"),
    prompt: "",
  },
  {
    id: "boy-band-lead",
    name: "Boy Band",
    appearance: "male",
    genres: ["children", "pop"],
    imageUrl: voiceAvatar("gen-boy-singer2"),
    prompt: "",
  },
  {
    id: "rnb-star",
    name: "R&B Star",
    appearance: "male",
    genres: ["r&b", "hip hop", "soul", "pop"],
    imageUrl: "https://media.solmi.ai/character-presets/rnb-star-v1.png",
    prompt:
      "Photorealistic portrait of a confident Black male singer in his " +
      "late 20s with a short fade haircut and a light beard, wearing a " +
      "stylish bomber jacket and a thin chain, singing passionately into a " +
      "handheld microphone on a concert stage, moody purple and blue stage " +
      "lighting with soft bokeh, shallow depth of field, professional music " +
      "photography, 3:4 portrait framing, no text, no watermark.",
  },
  {
    id: "soul-diva",
    name: "Soul Diva",
    appearance: "female",
    genres: ["soul", "r&b", "gospel", "pop", "jazz"],
    imageUrl: "https://media.solmi.ai/character-presets/soul-diva-v1.png",
    prompt:
      "Photorealistic portrait of a radiant Black female singer in her " +
      "20s with natural curly hair and gold hoop earrings, wearing an " +
      "elegant sequined dress, singing joyfully into a vintage microphone " +
      "on stage, warm golden spotlights with soft bokeh, shallow depth of " +
      "field, professional music photography, 3:4 portrait framing, no " +
      "text, no watermark.",
  },
  {
    id: "nordic-singer",
    name: "Nordic Singer",
    appearance: "male",
    genres: ["pop", "indie", "electronic", "rock"],
    imageUrl: "https://media.solmi.ai/character-presets/nordic-singer-v1.png",
    prompt:
      "Photorealistic portrait of a Northern European white male singer in " +
      "his late 20s with light blond hair, blue eyes and fair skin, wearing " +
      "a dark knit sweater, singing into a studio condenser microphone with " +
      "headphones on in a cozy recording studio, soft warm studio lighting " +
      "with blurred equipment in the background, shallow depth of field, " +
      "professional music photography, 3:4 portrait framing, no text, no " +
      "watermark.",
  },
  {
    id: "nordic-star",
    name: "Nordic Star",
    appearance: "female",
    genres: ["pop", "indie", "folk", "electronic"],
    imageUrl: "https://media.solmi.ai/character-presets/nordic-star-v1.png",
    prompt:
      "Photorealistic portrait of a Northern European white female singer " +
      "in her mid 20s with pale blond hair in a loose braid, light blue " +
      "eyes and fair skin, wearing a minimalist white top and delicate " +
      "silver jewelry, singing into a handheld microphone on stage, cool " +
      "icy-blue and white stage lighting with soft bokeh, shallow depth of " +
      "field, professional music photography, 3:4 portrait framing, no " +
      "text, no watermark.",
  },
  {
    id: "cowboy-cat",
    name: "Cowboy Cat",
    appearance: "none",
    genres: ["country", "folk", "americana", "novelty"],
    // Curated one-off asset (not generated by the preset script) — hosted
    // immutable, so any replacement image must bump the version suffix.
    imageUrl: "https://media.solmi.ai/character-presets/cowboy-cat-guitar-v1.png",
    prompt: "",
  },
  {
    id: "cool-cat",
    name: "Cool Cat",
    appearance: "none",
    genres: ["hip hop", "jazz", "funk", "novelty"],
    imageUrl: presetImage("cool-cat"),
    prompt:
      "A confident grey tabby cat mascot standing upright wearing small " +
      "black sunglasses and a gold chain, one paw raised in a cool greeting, " +
      "mint-green backdrop. " + MASCOT_SUFFIX,
  },
  {
    id: "robo-star",
    name: "Robo Star",
    appearance: "none",
    genres: ["electronic", "edm", "synthwave", "novelty"],
    imageUrl: presetImage("robo-star"),
    prompt:
      "A friendly retro-futuristic robot performer with a rounded white and " +
      "sky-blue chassis, glowing teal eyes on a small screen face, holding a " +
      "vintage microphone, light-blue backdrop. " + MASCOT_SUFFIX,
  },
];

/**
 * Whether a characterImageUrl is one of our stock preset/roster avatars (as
 * opposed to a photo the user uploaded). Matches the preset roster above plus
 * the hosting prefixes all roster generations have lived under — including
 * the legacy solmi.ai/voice-avatars origin hardcoded in shipped mobile
 * builds. User uploads are stored under different storage prefixes and never
 * match. Used to tell "the user deliberately cast this person" (upload) from
 * "the client silently defaulted a preset" (Mike Gardy case, Aug 15 2026:
 * a 'no human lead singer' project got the stock lead-singer avatar anchored
 * onto its Jesus reference images).
 */
export function isStockCharacterPresetUrl(url: string | null | undefined): boolean {
  const value = (url || "").trim();
  if (!value) return false;
  if (CHARACTER_PRESETS.some((preset) => preset.imageUrl === value)) return true;
  try {
    const { hostname, pathname } = new URL(value);
    const stockHost =
      hostname === "media.solmi.ai" ||
      hostname === "solmi.ai" ||
      hostname === "www.solmi.ai" ||
      hostname === "aisongmaker-bucket.s3.us-west-1.amazonaws.com";
    if (!stockHost) return false;
    return (
      pathname.startsWith("/voice-avatars/") ||
      pathname.startsWith("/character-presets/")
    );
  } catch {
    return false;
  }
}

/** Presets whose images have been generated and pinned — the only ones the
 * picker should show. */
export function availableCharacterPresets(): CharacterPreset[] {
  return CHARACTER_PRESETS.filter((preset) => !!preset.imageUrl);
}

/** Rank presets for display: genre matches for the current song first,
 * otherwise the curated order. Mascots keep their curated position so the
 * gallery always reads "real performers first, fun ones after". */
export function rankCharacterPresets(
  presets: CharacterPreset[],
  songGenre?: string | null,
): CharacterPreset[] {
  const genre = (songGenre || "").trim().toLowerCase();
  if (!genre) return presets;
  const matches = (preset: CharacterPreset) =>
    preset.genres.some(
      (candidate) => genre.includes(candidate) || candidate.includes(genre),
    );
  return [...presets].sort((a, b) => {
    const aHit = matches(a) ? 1 : 0;
    const bHit = matches(b) ? 1 : 0;
    return bHit - aHit;
  });
}
